import { RingBuffer } from "ringbuf.js";

/**
 * Create a SharedArrayBuffer with the appropriate size for a ring buffer
 * with the given capacity of Float32Array elements.
 *
 * @param capacity - The number of Float32 elements the buffer should hold
 * @returns A SharedArrayBuffer sized appropriately for the ring buffer
 */
export function createRingBufferStorage(capacity: number): SharedArrayBuffer {
  return RingBuffer.getStorageForCapacity(capacity, Float32Array);
}

/**
 * Create a RingBuffer for Float32Array audio data from a SharedArrayBuffer.
 * This factory encapsulates the ringbuf.js library so consumers only need
 * to import from this module.
 *
 * @param sharedArrayBuffer - The SharedArrayBuffer to use for the ring buffer
 * @returns A RingBuffer configured for Float32Array audio data
 */
export function createAudioRingBuffer(
  sharedArrayBuffer: SharedArrayBuffer
): RingBuffer {
  return new RingBuffer(sharedArrayBuffer, Float32Array);
}

export function interleave(input: Float32Array[], output: Float32Array, sampleCount: number) {
  const channels = input.length;
  const requiredOutputLength = sampleCount * channels;

  // Check that output has enough space for the requested frame count
  if (output.length < requiredOutputLength) {
    throw RangeError(`output too small: need ${requiredOutputLength} samples for ${sampleCount} frames × ${channels} channels, got ${output.length}`);
  }

  // Check that input buffers have enough samples
  if (input[0].length < sampleCount) {
    throw RangeError(`input buffers too small: need ${sampleCount} frames, got ${input[0].length}`);
  }

  let out_idx = 0;
  for (let i = 0; i < sampleCount; i++) {
    for (let channel = 0; channel < channels; channel++) {
      output[out_idx] = input[channel][i];
      out_idx++;
    }
  }
}

export function deinterleave(input: Float32Array, output: Float32Array[], sampleCount: number) {
  const channels = output.length;
  const requiredInputLength = sampleCount * channels;

  // Check that input has enough samples for the requested frame count
  if (input.length < requiredInputLength) {
    throw RangeError(`input too small: need ${requiredInputLength} samples for ${sampleCount} frames × ${channels} channels, got ${input.length}`);
  }

  // Check that output buffers are large enough
  if (output[0].length < sampleCount) {
    throw RangeError(`output buffers too small: need ${sampleCount} frames, got ${output[0].length}`);
  }

  for (let i = 0; i < channels; i++) {
    const out_channel = output[i];
    let interleaved_idx = i;
    for (let j = 0; j < sampleCount; ++j) {
      out_channel[j] = input[interleaved_idx];
      interleaved_idx += channels;
    }
  }
}

/**
 * Send interleaved audio frames to another thread, wait-free.
 *
 * These classes allow communicating between a non-real time thread (browser
 * main thread or worker) and a real-time thread (in an AudioWorkletProcessor).
 * Write and Reader cannot change role after setup, unless externally
 * synchronized.
 *
 * GC _can_ happen during the initial construction of this object when hopefully
 * no audio is being output. This depends on how implementations schedule GC
 * passes. After the setup phase no GC is triggered on either side of the queue.
 */
export class AudioWriter {
  ringbuf: RingBuffer

  /**
   * From a RingBuffer, build an object that can enqueue enqueue audio in a ring
   * buffer.
   * @constructor
   */
  constructor(ringbuf: RingBuffer) {
    if (ringbuf.type() !== "Float32Array") {
      throw TypeError("This class requires a ring buffer of Float32Array");
    }
    this.ringbuf = ringbuf;
  }
  /**
   * Enqueue a buffer of interleaved audio into the ring buffer.
   *
   *
   * Care should be taken to enqueue a number of samples that is a multiple of the
   * channel count of the audio stream.
   *
   * @param {Float32Array} buf An array of interleaved audio frames.
   *
   * @return The number of samples that have been successfuly written to the
   * queue. `buf` is not written to during this call, so the samples that
   * haven't been written to the queue are still available.
   */
  enqueue(buf: Float32Array, length?: number, offset?: number) {
    return this.ringbuf.push(buf, length, offset);
  }

  /**
   * @return The free space in the ring buffer. This is the amount of samples
   * that can be queued, with a guarantee of success.
   */
  availableWrite() {
    return this.ringbuf.availableWrite();
  }

  /**
   * Clears the ring buffer by resetting the read and writer pointers
   */
  clearBuffer() {
    // Access internal pointers to reset - they exist at runtime but are typed as private
    const rb = this.ringbuf as any;
    Atomics.store(rb.write_ptr, 0, 0)
    Atomics.store(rb.read_ptr, 0, 0)
  }
}

/**
 * Receive interleaved audio frames to another thread, wait-free.
 *
 * GC _can_ happen during the initial construction of this object when hopefully
 * no audio is being output. This depends on how implementations schedule GC
 * passes. After the setup phase no GC is triggered on either side of the queue.
 */
export class AudioReader {
  ringbuf: RingBuffer

  /**
   * From a RingBuffer, build an object that can dequeue audio in a ring
   * buffer.
   * @constructor
   */
  constructor(ringbuf: RingBuffer) {
    if (ringbuf.type() !== "Float32Array") {
      throw TypeError("This class requires a ring buffer of Float32Array");
    }
    this.ringbuf = ringbuf;
  }
  /**
   * Attempt to dequeue at most `buf.length` samples from the queue. This
   * returns the number of samples dequeued. If greater than 0, the samples are
   * at the beginning of `buf`.
   *
   * Care should be taken to dequeue a number of samples that is a multiple of the
   * channel count of the audio stream.
   *
   * @param {Float32Array} buf A buffer in which to copy the dequeued
   * interleaved audio frames.
   * @return The number of samples dequeued.
   */
  dequeue(buf: Float32Array, length?: number, offset?: number) {
    if (this.ringbuf.empty()) {
      return 0;
    }
    return this.ringbuf.pop(buf, length, offset);
  }
  /**
   * Query the occupied space in the queue.
   *
   * @return The amount of samples that can be read with a guarantee of success.
   *
   */
  availableRead() {
    return this.ringbuf.availableRead();
  }
}
